import { Model } from '@shared/models/Model';
import type { IModel } from '@shared/types/Model';
import type { ChipConfig, DataTableHeaders, DotNotationOf } from '@shared/types/Vuetify';
import type { MultiObjectInclude } from '@shared/types/ApiResponse';
import { Property } from '@app/land-hold/property/models/Property';
import type { IVersionPartition } from '@app/land-hold/property/models/VersionPartition';
import { formatArea } from '@shared/utils/formatArea';

export enum PartitionStatus {
    Empty = 'empty',
    Full = 'full',
    Partial = 'partial',
    Overflow = 'overflow',
}

export enum VersionStatus {
    Active = 'active',
    Archived = 'archived',
}

export interface IPropertyVersion extends IModel {
    property_id: string;
    effective_year: number;
    end_year: number | null;
    property_area_ha: string;
    reason_for_change: string | null;
    status: VersionStatus;
    years_display: string;
    is_editable: boolean;
    is_locked: boolean;
    partitioned_area: string;
    unpartitioned_area: string;
    partition_status: PartitionStatus;
    version_number: number;
    version_partitions: MultiObjectInclude<IVersionPartition>;
}

export type IndexPropertyVersionIncludes = IPropertyVersion;

export class PropertyVersion extends Model implements IPropertyVersion {
    property_id!: string;
    effective_year!: number;
    end_year!: number | null;
    property_area_ha!: string;
    reason_for_change!: string | null;
    status!: VersionStatus;
    years_display!: string;
    is_editable!: boolean;
    is_locked!: boolean;
    partitioned_area!: string;
    unpartitioned_area!: string;
    partition_status!: PartitionStatus;
    version_number!: number;
    version_partitions!: MultiObjectInclude<IVersionPartition>;

    public constructor(o: IPropertyVersion) {
        super(o);
        Object.assign(this, o);
    }

    public static override chips(): DotNotationOf<IPropertyVersion>[] {
        return ['partition_status', 'status'];
    }

    public static override chipConfig(field: DotNotationOf<IPropertyVersion>, item: IPropertyVersion): ChipConfig {
        switch (field) {
            case 'partition_status':
                return { size: 'small', color: Property.getStatusColor(item.partition_status) };
            case 'status':
                return {
                    size: 'small',
                    color: item.status === VersionStatus.Active ? 'success' : 'info',
                };
            default:
                return { size: 'small' };
        }
    }

    public static override headers(): DataTableHeaders<IndexPropertyVersionIncludes> {
        return [
            {
                title: 'Actions',
                key: 'actions',
                sortable: false,
                fixed: true,
                width: 120,
                minWidth: '120',
                headerProps: { style: { borderRight: '1px solid rgba(0, 0, 0, 0.12)' } },
                cellProps() {
                    return { style: { borderRight: '1px solid rgba(0, 0, 0, 0.12)' } };
                },
            },
            { title: 'Effective Year', key: 'effective_year' },
            { title: 'Years', key: 'years_display', sortable: false },
            { title: 'Status', key: 'status', sortable: false },
            { title: 'Area (ha)', key: 'property_area_ha', align: 'center', value: (item: IPropertyVersion) => formatArea(item.property_area_ha) },
            { title: 'Partitioned Area (ha)', key: 'partitioned_area', sortable: false, align: 'center', value: (item: IPropertyVersion) => formatArea(item.partitioned_area) },
            { title: 'Unpartitioned Area (ha)', key: 'unpartitioned_area', sortable: false, align: 'center', value: (item: IPropertyVersion) => formatArea(item.unpartitioned_area) },
            { title: 'Partition Status', key: 'partition_status', sortable: false },
            { title: 'Created At', key: 'created_at' },
            { title: 'Updated At', key: 'updated_at' },
        ];
    }

    public static override routes(): ReturnType<typeof Property.routes>['versions'] {
        return Property.routes().versions;
    }

    public static getDropdownLabel(version: IPropertyVersion): string {
        return `v${version.version_number} — ${version.years_display}`;
    }
}
